import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  getAdditionalUserInfo,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { getCurrentMillis } from '../utils/dateUtils';
import type { User, LoginData } from '../types/user';
import type { Subscription } from '../types/subscription';
import { subscriptionService } from './subscriptionService';
import { getFreePlan } from '../constants/subscriptionPlans';
import { SUBSCRIPTION_PLAN_IDS } from '../types/subscription';
import {
  clearPendingGoogleAccount,
  getPendingGoogleAccount,
  setPendingGoogleAccount,
  splitDisplayNameToThree,
  type PendingGoogleAccount,
} from '../utils/googleAuth';
import {
  clearPendingInviteRef,
  getPendingInviteRef,
} from './referralInviteService';
import { sanitizeReferralCode } from '../utils/referralAttribution';

export type GoogleSignInOutcome =
  | { type: 'success'; user: User }
  | { type: 'needs_registration_consent' }
  | { type: 'cancelled' };

export class AuthService {
  // Sign in with email and password, then fetch user data from Firestore
  static async signIn(loginData: LoginData): Promise<User> {
    try {
      console.log('Attempting to sign in with email:', loginData.email);
      console.log('Password length:', loginData.password.length);
      
      // First, authenticate with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(
        auth,
        loginData.email,
        loginData.password
      );

      console.log('Firebase Auth successful, user ID:', userCredential.user.uid);

      // Then fetch user data from Firestore
      const userDocRef = doc(db, 'Users', userCredential.user.uid);
      console.log('Fetching user document from:', `Users/${userCredential.user.uid}`);
      
      const userDoc = await getDoc(userDocRef);
      
      console.log('User document exists:', userDoc.exists());
      
      if (!userDoc.exists()) {
        console.error('User document not found for user ID:', userCredential.user.uid);
        console.log('Creating missing user document...');
        
        // Import trial extension service dynamically to avoid circular imports
        const { trialExtensionService } = await import('./trialExtensionService');
        
        // Get user registration date (use current date for existing users)
        const userRegistrationDate = new Date();
        
        // Create extended trial subscription using trial extension service
        const subscription = await trialExtensionService.createOrUpdateExtendedTrialSubscription(
          userCredential.user.uid,
          userRegistrationDate
        );

        // Create a basic user document for existing Firebase Auth users
        const basicUserData = {
          firstName: userCredential.user.displayName?.split(' ')[0] || '',
          middleName: '',
          lastName: userCredential.user.displayName?.split(' ').slice(1).join(' ') || '',
          email: userCredential.user.email || '',
          phoneNumber: userCredential.user.phoneNumber || '',
          phoneNumber2: '',
          nationalID: '',
          photoURL: userCredential.user.photoURL || '',
          birthdate: '',
          gender: '',
          martialStatus: '',
          address: '',
          city: '',
          country: '',
          referenceCode: '',
          titleID: '',
          managersIDs: [],
          officesIDsList: [],
          clientsReferences: [],
          teamMembersReferences: [],
          isLawyer: true,
          isAccountOwner: true,
          fcmToken: '',
          deviceInfo: {},
          isTermsConditionsAccepted: false,
          subscription: subscription,
          isArchived: false,
          isDeleted: false,
          createdAt: getCurrentMillis(),
          updatedAt: getCurrentMillis(),
          // Computed properties
          name: userCredential.user.displayName || userCredential.user.email || '',
          role: 'lawyer' as const,
          isActive: true
        };

        // Save the basic user document to Firestore
        await setDoc(userDocRef, basicUserData);
        console.log('Basic user document created successfully');

        // Create subscription document in Firestore
        await subscriptionService.createSubscription(subscription);
        console.log('Subscription document created successfully');

        // Note: Usage stats will be calculated locally from cache
        console.log('Usage stats created successfully');
        
        // Use the created data
        const user: User = {
          id: userCredential.user.uid,
          firstName: basicUserData.firstName,
          middleName: basicUserData.middleName,
          lastName: basicUserData.lastName,
          email: basicUserData.email,
          phoneNumber: basicUserData.phoneNumber,
          phoneNumber2: basicUserData.phoneNumber2,
          nationalID: basicUserData.nationalID,
          photoURL: basicUserData.photoURL,
          birthdate: basicUserData.birthdate,
          gender: basicUserData.gender,
          martialStatus: basicUserData.martialStatus,
          address: basicUserData.address,
          city: basicUserData.city || '',
          country: basicUserData.country || '',
          referenceCode: basicUserData.referenceCode,
          titleID: basicUserData.titleID,
          managersIDs: basicUserData.managersIDs,
          officesIDsList: basicUserData.officesIDsList,
          clientsReferences: basicUserData.clientsReferences,
          teamMembersReferences: basicUserData.teamMembersReferences,
          isLawyer: basicUserData.isLawyer,
          isAccountOwner: basicUserData.isAccountOwner,
          fcmToken: basicUserData.fcmToken,
          deviceInfo: basicUserData.deviceInfo,
          isTermsConditionsAccepted: basicUserData.isTermsConditionsAccepted,
          subscription: basicUserData.subscription,
          isArchived: basicUserData.isArchived,
          isDeleted: basicUserData.isDeleted,
          createdAt: basicUserData.createdAt,
          updatedAt: basicUserData.updatedAt,
          name: basicUserData.name,
          role: basicUserData.role,
          isActive: basicUserData.isActive
        };

        console.log('User authentication successful with created document');
        return user;
      }

      const userData = userDoc.data();
      console.log('User data retrieved:', userData);

      // Map the data from users collection to our User interface
      const user: User = {
        id: userCredential.user.uid, // Use Firebase UID as the user ID
        firstName: userData.firstName || '',
        middleName: userData.middleName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        phoneNumber: userData.phoneNumber || '',
        phoneNumber2: userData.phoneNumber2 || '',
        nationalID: userData.nationalID || '',
        photoURL: userData.photoURL || '',
        birthdate: userData.birthdate || '',
        gender: userData.gender || '',
        martialStatus: userData.martialStatus || '',
        address: userData.address || '',
        city: userData.city || '',
        country: userData.country || '',
        referenceCode: userData.referenceCode || '',
        titleID: userData.titleID || '',
        managersIDs: userData.managersIDs || [],
        officesIDsList: userData.officesIDsList || [],
        clientsReferences: userData.clientsReferences || [],
        teamMembersReferences: userData.teamMembersReferences || [],
        isLawyer: userData.isLawyer || false,
        isAccountOwner: userData.isAccountOwner || false,
        fcmToken: userData.fcmToken || '',
        deviceInfo: userData.deviceInfo || {},
        isTermsConditionsAccepted: userData.isTermsConditionsAccepted || false,
        subscription: userData.subscription || null,
        isArchived: userData.isArchived || false,
        isDeleted: userData.isDeleted || false,
        createdAt: userData.createdAt || '',
        updatedAt: userData.updatedAt || '',
        // Computed properties
        name: `${userData.firstName || ''} ${userData.middleName || ''} ${userData.lastName || ''}`.trim(),
        role: userData.isLawyer ? 'lawyer' : 'office_member',
        isActive: !userData.isArchived && !userData.isDeleted,
      };

      console.log('User object created:', user);

      // Check if user is active
      if (!user.isActive) {
        console.log('User account is inactive, signing out user');
        // Sign out the user since they're not active
        await signOut(auth);
        throw new Error('Your account has been deactivated. Please contact your administrator.');
      }

      console.log('User authentication successful');
      
      // Step 6: Fetch user's subscription data from Firestore
      console.log('🔍 AuthService: Fetching subscription data from /Subscriptions/{userId}/');
      const subscriptionDocRef = doc(db, 'Subscriptions', userCredential.user.uid);
      const subscriptionDoc = await getDoc(subscriptionDocRef);
      
      if (!subscriptionDoc.exists()) {
        console.log('📝 AuthService: No subscription document found, creating extended trial subscription');
        
        // Step 7: Create subscription using TrialExtensionService
        const { trialExtensionService } = await import('./trialExtensionService');
        const userRegistrationDate = new Date();
        
        const newSubscription = await trialExtensionService.createOrUpdateExtendedTrialSubscription(
          userCredential.user.uid,
          userRegistrationDate,
          user.name,
          user.phoneNumber
        );
        
        console.log('✅ AuthService: Extended trial subscription created:', newSubscription);
        
        // Update user object with new subscription
        user.subscription = newSubscription;
        
        // Return user with subscription data and flag to show trial modal
        return { ...user, shouldShowTrialModal: true };
      } else {
        console.log('✅ AuthService: Subscription document found');
        const subscriptionData = subscriptionDoc.data();
        console.log('📊 AuthService: Raw subscription data:', subscriptionData);
        
        // Update user object with subscription data
        user.subscription = subscriptionData as Subscription;
        console.log('📊 AuthService: User object with subscription:', {
          id: user.id,
          email: user.email,
          subscription: user.subscription
        });
        
        // Step 8: Check if trial is active or subscription plan is active
        const now = Date.now();
        console.log('🔍 AuthService: Checking trial status with data:', {
          status: subscriptionData.status,
          endDate: subscriptionData.endDate,
          currentTime: now
        });
        
        const isTrialActive = subscriptionData.status === 'trial' && 
          subscriptionData.endDate && 
          now < parseInt(subscriptionData.endDate);
        
        const isSubscriptionActive = subscriptionData.status === 'active' && 
          subscriptionData.endDate && 
          now < parseInt(subscriptionData.endDate);
        
        console.log('🔍 AuthService: Subscription status check:', {
          isTrialActive,
          isSubscriptionActive,
          endDate: subscriptionData.endDate,
          currentTime: now
        });
        
        if (isTrialActive) {
          console.log('🎯 AuthService: User is in trial period, showing trial modal');
          console.log('🎯 AuthService: Returning user with shouldShowTrialModal: true');
          return { ...user, shouldShowTrialModal: true };
        } else if (isSubscriptionActive) {
          console.log('✅ AuthService: User has active subscription, proceeding to dashboard');
          console.log('✅ AuthService: Returning user with shouldShowTrialModal: false');
          return { ...user, shouldShowTrialModal: false };
        } else {
          console.log('⚠️ AuthService: Subscription is not active, showing trial modal');
          console.log('⚠️ AuthService: Returning user with shouldShowTrialModal: true');
          return { ...user, shouldShowTrialModal: true };
        }
      }
    } catch (error: any) {
      console.error('User authentication error:', error);
      
      // Handle specific Firebase Auth errors
      if (error.code === 'auth/invalid-credential') {
        throw new Error('Invalid email or password. Please check your credentials and try again.');
      } else if (error.code === 'auth/user-not-found') {
        throw new Error('No account found with this email address. Please sign up first.');
      } else if (error.code === 'auth/wrong-password') {
        throw new Error('Incorrect password. Please try again.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Invalid email address format.');
      } else if (error.code === 'auth/too-many-requests') {
        throw new Error('Too many failed attempts. Please try again later.');
      } else if (error.code === 'auth/user-disabled') {
        throw new Error('This account has been disabled. Please contact support.');
      } else if (error instanceof Error) {
        throw new Error(error.message);
      }
      throw new Error('Login failed. Please check your credentials and try again.');
    }
  }

  /**
   * Login-only Google (OW-185). Existing Users/{uid} → success.
   * Brand-new Firebase Google user → stash pending, sign out, needs_registration_consent.
   */
  static async signInWithGoogle(deferNewUserUntilConsent = true): Promise<GoogleSignInOutcome> {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    try {
      const result = await signInWithPopup(auth, provider);
      const firebaseUser = result.user;
      const isNewUser = getAdditionalUserInfo(result)?.isNewUser === true;

      const userDoc = await getDoc(doc(db, 'Users', firebaseUser.uid));
      if (userDoc.exists()) {
        const user = await this.getCurrentUser();
        if (!user) throw new Error('Failed to load user profile');
        return { type: 'success', user };
      }

      // No Firestore profile: treat as new Google registration (Login-only flow).
      if (deferNewUserUntilConsent || isNewUser) {
        const pending: PendingGoogleAccount = {
          email: firebaseUser.email || '',
          displayName: firebaseUser.displayName || '',
          photoURL: firebaseUser.photoURL || '',
        };
        setPendingGoogleAccount(pending);
        await signOut(auth);
        return { type: 'needs_registration_consent' };
      }

      throw new Error('Google account has no Morafaa profile');
    } catch (error: any) {
      if (error?.code === 'auth/popup-closed-by-user' || error?.code === 'auth/cancelled-popup-request') {
        return { type: 'cancelled' };
      }
      if (error?.code === 'auth/account-exists-with-different-credential') {
        throw new Error(
          'This email is already registered with a different sign-in method. Please log in with email and password.'
        );
      }
      throw error;
    }
  }

  static async completeGoogleRegistration(input: {
    firstName: string;
    middleName: string;
    lastName: string;
    acceptTerms: boolean;
    acceptPrivacy: boolean;
  }): Promise<User> {
    if (!input.acceptTerms || !input.acceptPrivacy) {
      throw new Error('Terms and Privacy must be accepted');
    }
    const pending = getPendingGoogleAccount();
    if (!pending?.email) {
      throw new Error('Google Sign-In session expired. Please try again.');
    }

    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account', login_hint: pending.email });
    const result = await signInWithPopup(auth, provider);
    const firebaseUser = result.user;
    if ((firebaseUser.email || '').toLowerCase() !== pending.email.toLowerCase()) {
      await signOut(auth);
      throw new Error('Please select the same Google account you started with.');
    }

    const names = splitDisplayNameToThree(pending.displayName);
    const firstName = input.firstName || names.firstName;
    const middleName = input.middleName || names.middleName;
    const lastName = input.lastName || names.lastName;
    const now = getCurrentMillis();

    const userData: Omit<User, 'id'> = {
      firstName,
      middleName,
      lastName,
      email: pending.email,
      phoneNumber: '',
      phoneNumber2: '',
      nationalID: '',
      photoURL: pending.photoURL || '',
      birthdate: '',
      gender: '',
      martialStatus: '',
      address: '',
      city: '',
      country: 'Egypt',
      referenceCode: sanitizeReferralCode(getPendingInviteRef(), firebaseUser.uid),
      titleID: '1',
      managersIDs: [],
      officesIDsList: [],
      clientsReferences: [],
      teamMembersReferences: [],
      isLawyer: true,
      isAccountOwner: true,
      fcmToken: '',
      deviceInfo: {},
      isTermsConditionsAccepted: true,
      subscription: null,
      isArchived: false,
      isDeleted: false,
      createdAt: now,
      updatedAt: now,
      name: `${firstName} ${middleName} ${lastName}`.trim(),
      role: 'lawyer',
      isActive: true,
    };

    await setDoc(doc(db, 'Users', firebaseUser.uid), userData);
    clearPendingInviteRef();
    clearPendingGoogleAccount();

    try {
      const { TrialExtensionUtils } = await import('../constants/trialExtension');
      const registrationDate = new Date();
      const trialEndDate = TrialExtensionUtils.getTrialEndDate(registrationDate);
      const shouldGetExtendedTrial = TrialExtensionUtils.shouldGetExtendedTrial(registrationDate);
      const trialSubscriptionData = {
        userId: firebaseUser.uid,
        userName: userData.name,
        userPhone: '',
        planId: 'professional',
        status: 'trial' as const,
        startDate: Date.now().toString(),
        endDate: trialEndDate.getTime().toString(),
        totalPrice: 0,
        currency: 'EGP' as const,
        billingCycle: 'monthly' as const,
        isActive: true,
        trialExtensionReason: shouldGetExtendedTrial ? ('business_extension' as const) : ('standard_trial' as const),
        createdBy: firebaseUser.uid,
        updatedBy: firebaseUser.uid,
      };
      const createdSubscription = await subscriptionService.createSubscription(
        trialSubscriptionData as Omit<Subscription, 'id' | 'createdAt' | 'updatedAt'>
      );
      userData.subscription = createdSubscription;
      await setDoc(doc(db, 'Users', firebaseUser.uid), { ...userData, subscription: createdSubscription });
    } catch (e) {
      console.error('Google registration subscription create failed:', e);
    }

    const user = await this.getCurrentUser();
    if (!user) throw new Error('Failed to load user after Google registration');
    return user;
  }

  static async updateDemographics(fields: {
    phoneNumber: string;
    birthdate: string;
    gender: string;
  }): Promise<User> {
    const current = auth.currentUser;
    if (!current) throw new Error('Not authenticated');
    const ref = doc(db, 'Users', current.uid);
    try {
      await updateDoc(ref, {
        phoneNumber: fields.phoneNumber,
        birthdate: fields.birthdate,
        gender: fields.gender,
        updatedAt: getCurrentMillis(),
      });
    } catch (e) {
      // Local-first: still return merged profile for gate progress
      console.warn('Demographics Firestore update failed, continuing local-first', e);
    }
    const user = await this.getCurrentUser();
    if (!user) throw new Error('User not found');
    return {
      ...user,
      phoneNumber: fields.phoneNumber,
      birthdate: fields.birthdate,
      gender: fields.gender,
    };
  }

  static async updateProfilePhoto(photoURL: string): Promise<User> {
    const current = auth.currentUser;
    if (!current) throw new Error('Not authenticated');
    await updateDoc(doc(db, 'Users', current.uid), {
      photoURL,
      updatedAt: getCurrentMillis(),
    });
    const user = await this.getCurrentUser();
    if (!user) throw new Error('User not found');
    return { ...user, photoURL };
  }

  // Sign out the current user
  static async signOut(): Promise<void> {
    try {
      await signOut(auth);
      console.log('User signed out successfully');
    } catch (error) {
      console.error('Sign out error:', error);
      throw new Error('Failed to sign out');
    }
  }

  // Get current user data from Firestore
  static async getCurrentUser(): Promise<User | null> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return null;
      }

      const userDocRef = doc(db, 'Users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        return null;
      }

      const userData = userDoc.data();
      
      // Map the data from users collection to our User interface
      return {
        id: currentUser.uid, // Use Firebase UID as the user ID
        firstName: userData.firstName || '',
        middleName: userData.middleName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        phoneNumber: userData.phoneNumber || '',
        phoneNumber2: userData.phoneNumber2 || '',
        nationalID: userData.nationalID || '',
        photoURL: userData.photoURL || '',
        birthdate: userData.birthdate || '',
        gender: userData.gender || '',
        martialStatus: userData.martialStatus || '',
        address: userData.address || '',
        city: userData.city || '',
        country: userData.country || '',
        referenceCode: userData.referenceCode || '',
        titleID: userData.titleID || '',
        managersIDs: userData.managersIDs || [],
        officesIDsList: userData.officesIDsList || [],
        clientsReferences: userData.clientsReferences || [],
        teamMembersReferences: userData.teamMembersReferences || [],
        isLawyer: userData.isLawyer || false,
        isAccountOwner: userData.isAccountOwner || false,
        fcmToken: userData.fcmToken || '',
        deviceInfo: userData.deviceInfo || {},
        isTermsConditionsAccepted: userData.isTermsConditionsAccepted || false,
        subscription: userData.subscription || null,
        isArchived: userData.isArchived || false,
        isDeleted: userData.isDeleted || false,
        createdAt: userData.createdAt || '',
        updatedAt: userData.updatedAt || '',
        // Computed properties
        name: `${userData.firstName || ''} ${userData.middleName || ''} ${userData.lastName || ''}`.trim(),
        role: userData.isLawyer ? 'lawyer' : 'office_member',
        isActive: !userData.isArchived && !userData.isDeleted,
      };
    } catch (error) {
      console.error('Error fetching current user:', error);
      return null;
    }
  }

  // Send password reset email
  static async sendPasswordReset(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth, email);
      console.log('Password reset email sent to:', email);
    } catch (error) {
      console.error('Password reset error:', error);
      if (error instanceof Error) {
        throw new Error(error.message);
      }
      throw new Error('Failed to send password reset email');
    }
  }

  // Listen to authentication state changes
  static onAuthStateChanged(callback: (user: User | null) => void) {
    return onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('🔄 Firebase auth state changed:', firebaseUser);
      if (firebaseUser) {
        try {
          console.log('🔄 Firebase user found, fetching user data...');
          
          // Add retry mechanism for new users (document might not be immediately available)
          let user = await this.getCurrentUser();
          let retries = 0;
          const maxRetries = 3;
          
          while (!user && retries < maxRetries) {
            console.log(`🔄 User document not found, retrying... (${retries + 1}/${maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms
            user = await this.getCurrentUser();
            retries++;
          }
          
          console.log('🔄 User data fetched:', user);
          callback(user);
        } catch (error) {
          console.error('Error getting user data on auth state change:', error);
          callback(null);
        }
      } else {
        console.log('🔄 No Firebase user found');
        callback(null);
      }
    });
  }

  // Get subscription document for a user
  static async getSubscriptionDoc(userId: string) {
    const subscriptionDocRef = doc(db, 'Subscriptions', userId);
    return await getDoc(subscriptionDocRef);
  }
} 